/** /external-access — every external call, across partners and tenants (dev-platform
 *  metaplan G2; GATE P2).
 *
 * One row per call from outside: requests authenticated by a sandbox or partner key, MCP
 * calls, docs and developer-portal requests (External_Access_Log). Driven by the reusable
 * QuerySearch widget like /api-requests: the serialized query lives in `?q=`, a tenant
 * picker (`?tenant=12,845`, one or several operators, layered over the console tenant
 * scope) injects `tour_operator_id`. Server-side pagination + sorting (id / created_ts
 * only).
 *
 * The log is monthly partitioned and kept 13 months, so a `created` window is MANDATORY —
 * the backend 400s without one. The page seeds the shared 7-day window on first visit (no
 * `?q=`, no `?preset=`) and never fires a windowless query (it prompts instead).
 *
 * Read-only: no batch operations. A row opens its detail drawer in place (there is no detail
 * endpoint; the list row carries every column).
 */
import { Alert, Box, Button, CircularProgress, Stack, Typography } from '@mui/material';
import {
  DataGridPro,
  gridColumnFieldsSelector,
  useGridApiRef,
  type GridPaginationModel,
  type GridSlots,
  type GridToolbarProps,
} from '@mui/x-data-grid-pro';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import * as externalAccessApi from '@/api/externalAccess';
import type { ExternalAccessItem } from '@/api/externalAccess';
import { ConsoleApiError, isStatementTimeout } from '@/api/client';
import { orderColumns } from '@/components/DataGrid/columnOrder';
import { countLabelDisplayedRows } from '@/components/DataGrid/CountTotalLabel';
import { GridControlsToolbar } from '@/components/DataGrid/GridControlsToolbar';
import { SearchTimeoutAlert } from '@/components/DataGrid/SearchTimeoutAlert';
import { useCountMode } from '@/components/DataGrid/useCountMode';
import { gridRefreshToolbar, useGridAutoRefresh } from '@/components/DataGrid/useGridAutoRefresh';
import { PageHeader } from '@/components/PageHeader';
import {
  QuerySearchInput,
  equalityOperator,
  parse,
  serialize,
  toWireAst,
  type FilterNode,
} from '@/components/QuerySearch';
import { useSnackbar } from '@/components/Snackbar';
import { TenantPicker, type TenantPickerValue } from '@/components/TenantPicker/TenantPicker';
import { useGridPresets, type PresetFiltersAdapter } from '@/hooks/useGridPresets';
import { useGridCountMode } from '@/hooks/useUserPreferences';
import { PAGE_FILL_SX } from '@/layout/pageFill';
import { formatTenantIds } from '@/scope/tenantIds';
import { usePageTenant } from '@/scope/usePageTenant';
import { createdWindowNode, hasCreatedTimeBound } from '@/utils/gridLinks';

import { ExternalAccessDetailDrawer } from './ExternalAccessDetailDrawer';
import { buildColumns } from './externalAccessColumns';
import { buildExternalAccessNamespaces } from './externalAccessNamespaces';

const DEFAULT_PAGE_SIZE = 50;

const NO_TENANTS: TenantPickerValue[] = [];

const DEFAULT_HIDDEN = ['client', 'user_agent', 'id'];

/** Presets store the filter state as the raw `?q=` DSL text — it IS the canonical form
 *  (round-trips through parse/serialize), same as api-requests. */
const PRESET_FILTERS: PresetFiltersAdapter = {
  urlParams: ['q'],
  capture: (get) => {
    const q = get('q');
    return q ? { q } : undefined;
  },
  toUrlParams: (filters) => {
    const q = (filters as { q?: unknown } | null)?.q;
    return typeof q === 'string' && q ? { q } : ({} as Record<string, string>);
  },
};

/** Drop every `tenant` condition from a tree (the picker owns that filter). */
function stripTenantConds(nodes: FilterNode[]): { nodes: FilterNode[]; removed: boolean } {
  let removed = false;
  const walk = (list: FilterNode[]): FilterNode[] => {
    const out: FilterNode[] = [];
    for (const n of list) {
      if (n.kind === 'cond' && n.namespace === 'tenant') {
        removed = true;
        continue;
      }
      if (n.kind === 'group') {
        const children = walk(n.children);
        if (children.length === 0) continue;
        if (children.length === 1) out.push(children[0]!);
        else out.push({ ...n, children });
        continue;
      }
      out.push(n);
    }
    return out;
  };
  return { nodes: walk(nodes), removed };
}

export function ExternalAccessPage() {
  const snackbar = useSnackbar();
  const [sp, setSp] = useSearchParams();
  const apiRef = useGridApiRef();
  const namespaces = useMemo(() => buildExternalAccessNamespaces(), []);
  const [openRow, setOpenRow] = useState<ExternalAccessItem | null>(null);

  // Effective tenants: URL ?tenant= > console scope (src/scope/usePageTenant.ts),
  // a list (`?tenant=12,845`) — the page takes the whole scope.
  const pageTenant = usePageTenant({ multiple: true });
  // Drives the preset defaults' tenant COLUMN only (shown unless exactly one
  // tenant is named); the filtering value is resolved below, once ?q= has been
  // parsed and scope suppression is known.
  const defaultShowTenant = pageTenant.ids.length !== 1;

  const presets = useGridPresets({
    grid: 'external_access',
    defaultOrder: useMemo(
      () =>
        buildColumns({
          showTenant: defaultShowTenant,
          onQuickFilter: () => undefined,
          onOpen: () => undefined,
        }).map((c) => c.field),
      [defaultShowTenant],
    ),
    defaultHidden: DEFAULT_HIDDEN,
    defaultOrdering: '-created_ts',
    filters: PRESET_FILTERS,
  });

  const q = presets.getParam('q') ?? '';
  const ordering = presets.ordering;

  const { tree, diagnostics } = useMemo(() => {
    const r = parse(q, namespaces);
    return { tree: r.nodes, diagnostics: r.diagnostics };
  }, [q, namespaces]);

  const lastDiagKey = useRef<string>('');
  useEffect(() => {
    if (diagnostics.length === 0) return;
    const key = `${q}::${diagnostics.length}`;
    if (key === lastDiagKey.current) return;
    lastDiagKey.current = key;
    snackbar.show(`Some of your search couldn't be interpreted: ${diagnostics[0]!.message}`, 'warning');
  }, [diagnostics, q, snackbar]);

  // A `tenant=` condition in ?q= already names a tenant: the ambient console scope then
  // stands down rather than AND-ing a second, conflicting tenant onto the query. An
  // explicitly PICKED tenant still wins and strips those conditions (effect below).
  const queryNamesTenant = useMemo(() => stripTenantConds(tree).removed, [tree]);
  const tenants = pageTenant.fromScope && queryNamesTenant ? NO_TENANTS : pageTenant.values;
  /** What the API's tour_operator_id param carries: `'12,845'` (D1). */
  const tenantApiValue = formatTenantIds(tenants.map((t) => t.id)) ?? undefined;

  const [pagination, setPagination] = useState<GridPaginationModel>({ page: 0, pageSize: DEFAULT_PAGE_SIZE });
  const [extendedTimeout, setExtendedTimeout] = useState(false);

  useEffect(() => {
    setPagination((p) => ({ ...p, page: 0 }));
  }, [q, pageTenant.raw]);

  const { beforeUrlWrite } = presets;
  const setQuery = useCallback(
    (nodes: FilterNode[]) => {
      const next = new URLSearchParams(sp);
      beforeUrlWrite(next);
      const str = serialize(nodes);
      if (str) next.set('q', str);
      else next.delete('q');
      setSp(next, { replace: true });
    },
    [sp, setSp, beforeUrlWrite],
  );

  // Seed the default bounded `created` window on first visit — only when neither an
  // explicit query nor a preset drives the page.
  const seededRef = useRef(false);
  useEffect(() => {
    if (seededRef.current) return;
    seededRef.current = true;
    if (sp.get('q') !== null || sp.get('preset') !== null) return;
    setQuery([createdWindowNode()]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // When a tenant is picked and the tree still carries tenant conditions, strip them and
  // rewrite ?q= (the picker is authoritative for tenant).
  useEffect(() => {
    // Never for a scope-derived tenant: the console scope must not rewrite the user's (or a
    // preset's) query — that would also diverge a loaded preset.
    if (!tenantApiValue || pageTenant.fromScope) return;
    const { nodes, removed } = stripTenantConds(tree);
    if (removed) {
      const next = new URLSearchParams(sp);
      presets.beforeUrlWrite(next);
      const str = serialize(nodes);
      if (str) next.set('q', str);
      else next.delete('q');
      setSp(next, { replace: true });
      snackbar.show('Tenant filter replaced by the picker above.', 'info');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenantApiValue, tree]);

  // Quick filters from cell popovers: append an equality chip for the namespace (tenant
  // goes through the picker instead — its chip would be stripped right back out).
  const onQuickFilter = useCallback(
    (namespace: string, value: string) => {
      if (namespace === 'tenant') {
        pageTenant.setRaw(value);
        return;
      }
      const cfg = namespaces[namespace];
      const operator = cfg ? equalityOperator(cfg.operators) : null;
      if (!operator) return;
      setQuery([...tree, { kind: 'cond', namespace, operator, value }]);
    },
    [namespaces, tree, setQuery, pageTenant],
  );

  const lockedFilters = tenantApiValue
    ? { tenant: { operator: 'iexact', value: tenantApiValue } }
    : undefined;
  // The suggestion endpoints here are the bookings autocompletes, which take the list.
  const extraFilterValues = tenantApiValue ? { tour_operator_id: tenantApiValue } : undefined;

  // The backend REQUIRES a created time bound on every search — mirror the rule here so a
  // windowless query never fires (deleted chip, hand-edited ?q=, windowless preset).
  const hasWindow = useMemo(() => hasCreatedTimeBound(tree), [tree]);

  const countPref = useGridCountMode('external_access');

  // The filter identity shared by the list call and the count-only call, so the total can
  // never describe a different set than the rows on screen.
  const countOnlyParams = useMemo(
    () => ({
      ordering,
      ...(tenantApiValue ? { tour_operator_id: tenantApiValue } : {}),
      filters: toWireAst(tree),
    }),
    [ordering, tenantApiValue, tree],
  );

  const autoRefresh = useGridAutoRefresh({ grid: 'external_access' });
  const query = useQuery({
    queryKey: [
      'external-access',
      pagination.page,
      pagination.pageSize,
      ordering,
      tenantApiValue ?? null,
      q,
      extendedTimeout,
      countPref.mode,
    ],
    queryFn: () =>
      externalAccessApi.list({
        ...countOnlyParams,
        page: pagination.page + 1,
        page_size: pagination.pageSize,
        ...(extendedTimeout ? { timeout: 'extended' as const } : {}),
        // Only when the user overrode the mode; omitting it keeps the backend's own default.
        ...(countPref.mode ? { count_mode: countPref.mode } : {}),
      }),
    placeholderData: keepPreviousData,
    ...autoRefresh.queryOptions(extendedTimeout),
    enabled: !presets.blocking && hasWindow,
  });

  const rows = useMemo(() => query.data?.results ?? [], [query.data?.results]);

  const queryIdentity = useMemo(
    () => JSON.stringify({ q, tenant: tenantApiValue, ordering, pageSize: pagination.pageSize }),
    [q, tenantApiValue, ordering, pagination.pageSize],
  );

  const count = useCountMode({
    pref: countPref,
    queryIdentity,
    response: query.data,
    countOnly: () => externalAccessApi.count(countOnlyParams),
    page: pagination.page + 1,
    pageSize: pagination.pageSize,
    ...(query.data ? { rowsOnPage: query.data.results.length } : {}),
    onResetPage: () => setPagination((p) => ({ ...p, page: 0 })),
  });

  const lastIdentityRef = useRef(queryIdentity);
  useEffect(() => {
    if (lastIdentityRef.current !== queryIdentity) {
      lastIdentityRef.current = queryIdentity;
      setExtendedTimeout(false);
    }
  }, [queryIdentity]);

  const columns = useMemo(
    () =>
      orderColumns(
        buildColumns({ showTenant: tenants.length !== 1, onQuickFilter, onOpen: setOpenRow }),
        presets.orderedFields,
      ),
    [tenants.length, onQuickFilter, presets.orderedFields],
  );

  const apiError =
    query.error instanceof ConsoleApiError && query.error.status === 400 && !isStatementTimeout(query.error)
      ? query.error
      : null;
  const backendMessage =
    (apiError?.body as { filters?: string } | undefined)?.filters ?? apiError?.message ?? null;

  const shownOnce = useRef(false);
  useEffect(() => {
    if (backendMessage && !shownOnce.current) {
      shownOnce.current = true;
      snackbar.show(`Search rejected: ${backendMessage}`, 'error');
    }
    if (!backendMessage) shownOnce.current = false;
  }, [backendMessage, snackbar]);

  const hasSearch = q.trim().length > 0 || tenants.length > 0;

  return (
    <Box sx={PAGE_FILL_SX}>
      <PageHeader
        helpStory="external-access"
        title="External Access"
        subtitle="Every call from outside — partner and sandbox keys, MCP, docs and the developer portal — attributed to its key, partner, user and tenant. Kept 13 months; every search needs a time window."
      />

      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} sx={{ mb: 2, alignItems: { sm: 'flex-start' } }}>
        <Box sx={{ width: { xs: '100%', sm: 280 }, flexShrink: 0 }}>
          <TenantPicker
            multiple
            fieldWidth="100%"
            value={tenants}
            onChange={pageTenant.setTenants}
          />
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <QuerySearchInput
            namespaces={namespaces}
            value={tree}
            onChange={setQuery}
            {...(lockedFilters ? { lockedFilters } : {})}
            {...(extraFilterValues ? { extraFilterValues } : {})}
            placeholder="Search calls…  e.g. partner=acme & outcome=denied"
          />
        </Box>
      </Stack>

      {isStatementTimeout(query.error) && (
        <SearchTimeoutAlert
          extendedAttempted={extendedTimeout}
          onRetryExtended={!extendedTimeout ? () => setExtendedTimeout(true) : undefined}
          retrying={query.isFetching}
        />
      )}

      {backendMessage && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {backendMessage}
        </Alert>
      )}

      {!hasWindow && (
        <Alert
          severity="warning"
          sx={{ mb: 2 }}
          action={
            <Button color="inherit" size="small" onClick={() => setQuery([...tree, createdWindowNode()])}>
              Add last 7 days
            </Button>
          }
        >
          Every search needs a created time window — the access log is kept in monthly partitions
          and is too large to scan unbounded.
        </Alert>
      )}

      {presets.blocking ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 6 }}>
          <CircularProgress />
        </Box>
      ) : (
        <DataGridPro<ExternalAccessItem>
          apiRef={apiRef}
          rows={rows}
          rowCount={count.rowCount}
          {...(count.paginationMeta ? { paginationMeta: count.paginationMeta } : {})}
          getRowId={(r) => r.id}
          columns={columns}
          loading={query.isLoading || query.isFetching}
          sx={{ flex: 1, minHeight: 360, '& .MuiDataGrid-row': { cursor: 'pointer' } }}
          onRowClick={(p) => setOpenRow(p.row)}
          pagination
          paginationMode="server"
          sortingMode="server"
          paginationModel={pagination}
          onPaginationModelChange={setPagination}
          sortModel={presets.sortModel}
          onSortModelChange={presets.onSortModelChange}
          columnVisibilityModel={presets.columnVisibilityModel}
          onColumnVisibilityModelChange={presets.onColumnVisibilityModelChange}
          onColumnOrderChange={() => presets.onColumnOrderChange(gridColumnFieldsSelector(apiRef))}
          pageSizeOptions={[25, 50, 100, 200]}
          disableRowSelectionOnClick
          disableColumnFilter
          slots={{
            noRowsOverlay: () => (
              <Box sx={{ p: 4, textAlign: 'center' }}>
                {hasSearch ? 'No external calls match this search.' : 'Search to find external calls.'}
              </Box>
            ),
            toolbar: GridControlsToolbar as GridSlots['toolbar'],
          }}
          slotProps={{
            loadingOverlay: { variant: 'linear-progress', noRowsVariant: 'skeleton' },
            pagination: {
              labelDisplayedRows: countLabelDisplayedRows({
                state: count.state,
                total: count.total,
                canRequest: true,
                onRequest: count.requestTotal,
              }),
            },
            toolbar: {
              // No time window → the list query is disabled (and refetch() ignores that).
              refresh: gridRefreshToolbar(query, autoRefresh, { paused: extendedTimeout, disabled: !hasWindow }),
              hideFilterButton: true,
              exportFileName: 'external-access',
              presets: presets.toolbar,
              countMode: { mode: count.mode, setMode: count.setMode },
            } as GridToolbarProps,
          }}
        />
      )}

      {query.data?.count_is_capped && (
        <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
          10,000+ results — refine your search to see more precise counts.
        </Typography>
      )}

      <ExternalAccessDetailDrawer row={openRow} onClose={() => setOpenRow(null)} />
    </Box>
  );
}
